import { MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DistributionSummary, DistributionAction } from '@/types/distribution';
import {
  DISTRIBUTION_STATUS_LABELS,
  DISTRIBUTION_STATUS_COLORS,
  ACTION_CONFIG,
  getAvailableActions,
} from '@/components/distributions/distributionStatus';
import { formatCurrency, formatDate } from '@/utils/formatters';

const GL_STATUS_VARIANTS: Record<
  string,
  'secondary' | 'default' | 'destructive' | 'outline'
> = {
  DRAFT: 'secondary',
  ISSUED: 'outline',
  APPROVED: 'default',
  POSTED: 'default',
  REVERSED: 'destructive',
};

interface DistributionMenuItemsProps {
  status: DistributionSummary['status'];
  distributionId: string;
  onAction: (action: DistributionAction, id: string) => void;
}

function DistributionMenuItems({
  status,
  distributionId,
  onAction,
}: DistributionMenuItemsProps) {
  const actions = getAvailableActions(status);
  const nonDestructive = actions.filter((a) => !ACTION_CONFIG[a].destructive);
  const destructive = actions.filter((a) => ACTION_CONFIG[a].destructive);

  return (
    <>
      {nonDestructive.map((action) => {
        const config = ACTION_CONFIG[action];
        return (
          <DropdownMenuItem
            key={action}
            onClick={() => onAction(action, distributionId)}
          >
            <config.icon className="mr-2 h-4 w-4" />
            {config.label}
          </DropdownMenuItem>
        );
      })}
      {destructive.length > 0 && (
        <>
          <DropdownMenuSeparator />
          {destructive.map((action) => {
            const config = ACTION_CONFIG[action];
            return (
              <DropdownMenuItem
                key={action}
                onClick={() => onAction(action, distributionId)}
                className="text-destructive"
              >
                <config.icon className="mr-2 h-4 w-4" />
                {config.label}
              </DropdownMenuItem>
            );
          })}
        </>
      )}
    </>
  );
}

interface DistributionsListProps {
  distributions: DistributionSummary[];
  showFundColumn?: boolean;
  onNavigate?: (id: string) => void;
  onCancel?: (id: string) => void;
}

export function DistributionsList({
  distributions,
  showFundColumn = false,
  onNavigate,
  onCancel,
}: DistributionsListProps) {
  const handleAction = (action: DistributionAction, id: string) => {
    if (action === 'cancel') {
      onCancel?.(id);
    } else {
      onNavigate?.(id);
    }
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>#</TableHead>
          {showFundColumn && <TableHead>Fund</TableHead>}
          <TableHead>Transaction</TableHead>
          <TableHead>Date</TableHead>
          <TableHead className="text-right">Amount</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>GL</TableHead>
          <TableHead className="w-[50px]"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {distributions.map((distribution) => (
          <TableRow key={distribution.id}>
            <TableCell className="font-medium">
              {distribution.distribution_number}
            </TableCell>
            {showFundColumn && (
              <TableCell>
                <span className="text-sm">{distribution.fund_name}</span>
              </TableCell>
            )}
            <TableCell>
              {distribution.description || (
                <span className="text-muted-foreground">&mdash;</span>
              )}
            </TableCell>
            <TableCell>{formatDate(distribution.distribution_date)}</TableCell>
            <TableCell className="text-right font-medium">
              {formatCurrency(distribution.amount)}
            </TableCell>
            <TableCell>
              <Badge variant={DISTRIBUTION_STATUS_COLORS[distribution.status]}>
                {DISTRIBUTION_STATUS_LABELS[distribution.status]}
              </Badge>
            </TableCell>
            <TableCell>
              {distribution.fund_transaction_status ? (
                <Badge
                  variant={
                    GL_STATUS_VARIANTS[distribution.fund_transaction_status] ??
                    'outline'
                  }
                >
                  {distribution.fund_transaction_status}
                </Badge>
              ) : (
                <span className="text-muted-foreground text-xs">-</span>
              )}
            </TableCell>
            <TableCell>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    aria-label={`Actions for distribution ${distribution.distribution_number}`}
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DistributionMenuItems
                    status={distribution.status}
                    distributionId={distribution.id}
                    onAction={handleAction}
                  />
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
